import React, { useCallback, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBedSofaIcon from 'LuxKit/Icons/line/LineBedSofaIcon'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineSignAltIcon from 'LuxKit/Icons/line/LineSignAltIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

interface Props {
  hotelCity: string
  tripPaces: Array<{
    tripPace: string
    selected: boolean
  }>
  onPaceSelect: (pace: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripPaceSelection({
  hotelCity,
  tripPaces,
  onPaceSelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((pace: string) => {
    onPaceSelect(pace)
    onNext()
  }, [onPaceSelect, onNext])

  const tripPaceMap = useMemo(() =>
    arrayToMap(tripPaces, pace => pace.tripPace, pace => pace.selected),
  [tripPaces])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'trip_pace',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const TRIP_PACES = [
    {
      tripPace: 'Relaxed',
      title: intl.formatMessage({
        id: '0LzOB3MZvz',
        defaultMessage: 'Relaxed',
        description: 'Title for relaxed trip pace option',
      }),
      description: intl.formatMessage({
        id: 'Cs9nTG3T3o',
        defaultMessage: 'Take your time, enjoy the vibe.',
        description: 'Description for relaxed trip pace option',
      }),
      icon: <LineBedSofaIcon />,
    },
    {
      tripPace: 'Balanced',
      title: intl.formatMessage({
        id: 'eWJ3EMybKk',
        defaultMessage: 'Explore a little',
        description: 'Title for balanced trip pace option',
      }),
      description: intl.formatMessage({
        id: 'uh5+vG1Lel',
        defaultMessage: 'A mix of activities and relaxation.',
        description: 'Description for balanced trip pace option',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      tripPace: 'Active',
      title: intl.formatMessage({
        id: '34RcGm4hIf',
        defaultMessage: 'Active',
        description: 'Title for active trip pace option',
      }),
      description: intl.formatMessage({
        id: 'M9380D1UbJ',
        defaultMessage: 'Pack in as much as possible.',
        description: 'Description for active trip pace option',
      }),
      icon: <LineSignAltIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'USyUMMHwFp',
      defaultMessage: 'Trip <i>Pace</i>',
      description: 'Heading text with italicized Pace',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage(
    {
      id: '6CW1jVv+2G',
      defaultMessage: 'What kind of tempo are you after for your {hotelCity} trip?',
      description: 'Question asking about preferred trip tempo',
    },
    { hotelCity },
  )

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      <TileCardsGroup>
        {TRIP_PACES.map(pace => (
          <TileCard
            key={pace.tripPace}
            title={pace.title}
            subtitle={pace.description}
            icon={pace.icon}
            value={pace.tripPace}
            onClick={handleOnClick}
            selected={!!tripPaceMap.get(pace.tripPace)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripPaceSelection
